import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, TrendingUp, Shield, Check, CheckCircle, ChevronDown,
  Radio, LogIn, Zap, Target, Clock, Star, ShieldCheck, X, Database, LineChart,
  Activity, AlertCircle, TrendingDown, Mail, DollarSign, Lock,
  Droplets, Bell,
} from 'lucide-react';
import { useAuthContext } from '../context/AuthContext';

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        obs.disconnect();
      }
    }, { threshold: 0.15, ...options });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
};

// Continuous visibility tracker — flips true/false as the element enters and
// leaves the viewport. Used to pause expensive rAF loops when the hero is
// scrolled off-screen so the rest of the page scrolls smoothly.
const useOnScreen = (ref, rootMargin = '120px') => {
  const [onScreen, setOnScreen] = useState(true);
  useEffect(() => {
    const el = ref && ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(
      ([entry]) => setOnScreen(entry.isIntersecting),
      { rootMargin, threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, rootMargin]);
  return onScreen;
};

// Animates a number from 0 to `target` over `durationMs` once `active` flips true.
const useCountUp = (target, durationMs, active) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf;
    const start = performance.now();
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      setValue(target * easeOut(t));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target, durationMs]);
  return value;
};

const AnimatedCounter = ({ target, suffix = '', inView }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const dur = 1600;
    const steps = 40;
    const inc = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += inc;
      if (current >= target) { setVal(target); clearInterval(timer); }
      else setVal(parseFloat(current.toFixed(1)));
    }, dur / steps);
    return () => clearInterval(timer);
  }, [inView, target]);
  return <span>{val}{suffix}</span>;
};
import ModalManager from '../components/modals/ModalManager';
import ToastContainer from '../components/ui/ToastContainer';

const faqs = [
  {
    q: 'Is this financial advice?',
    a: 'AlphaNQ is a live educational signal service. We demonstrate a rules-based, ML-optimized trading strategy in real time \u2014 we do not provide personalized investment advice. All trading involves risk, and past performance does not guarantee future results.',
  },
  {
    q: 'What exactly do I get with a subscription?',
    a: 'Full access to live signals during the New York session, the complete verified trade history, tier-based position sizing guidance, and real-time buy/sell alerts during market hours.',
  },
  {
    q: 'What do I need to get started?',
    a: 'A funded futures trading account with a broker that supports Nasdaq futures contracts. We recommend starting with at least $500 (Tier 1). You\'ll also need a stable internet connection for the live stream.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. There are no contracts or commitments. Cancel from your account page at any time and you won\'t be billed again.',
  },
  {
    q: 'What are the live session times?',
    a: 'We trade the New York session, 9:30 AM – 4:00 PM ET. This is the highest-liquidity window for Nasdaq futures, and it\'s the only session our algorithm is tuned for.',
  },
  {
    q: 'How is the win rate calculated?',
    a: 'Our 73.4% win rate is based on 1,352 backtested trades. Every entry, exit, and result is logged and available in the Backtest Results section for full transparency.',
  },
];

const signalFeed = [
  { time: '10:32 AM', type: 'BUY', entry: '18,392.50', target: '18,420.00', result: 'won', pnl: '+$137.50' },
  { time: '10:47 AM', type: 'SELL', entry: '18,418.25', target: '18,390.00', result: 'won', pnl: '+$141.25' },
  { time: '11:05 AM', type: 'BUY', entry: '18,385.00', target: '18,412.50', result: 'won', pnl: '+$137.50' },
  { time: '11:23 AM', type: 'SELL', entry: '18,410.75', target: '18,385.00', result: 'lost', pnl: '-$56.25' },
  { time: '11:41 AM', type: 'BUY', entry: '18,378.25', target: '18,405.00', result: 'won', pnl: '+$133.75' },
];

const testimonials = [
  {
    name: 'Marcus T.',
    role: 'Futures Trader, 2yr member',
    text: "The live signals during the NY session actually produce results. Went from fumbling around with indicators to just following the signals. Up $6k in my second month.",
    rating: 5,
  },
  {
    name: 'Sarah K.',
    role: 'Part-time Trader',
    text: "I work a 9-5 so the morning session is perfect. I check the signals on my break, place the trades, and move on with my day. Made back the subscription cost in two days.",
    rating: 5,
  },
  {
    name: 'David R.',
    role: 'Full-time Trader, 8 months',
    text: "Tried three other signal services before this. None of them posted their losses. AlphaNQ shows everything. That transparency is why I trust them with my capital.",
    rating: 5,
  },
];

const monthlyResults = [
  { month: 'Jun 2025', trades: 128, winRate: '70.3%', pnl: '+$3,840' },
  { month: 'Jul 2025', trades: 132, winRate: '71.2%', pnl: '+$4,240' },
  { month: 'Aug 2025', trades: 140, winRate: '72.9%', pnl: '+$4,680' },
  { month: 'Sep 2025', trades: 146, winRate: '73.3%', pnl: '+$4,490' },
  { month: 'Oct 2025', trades: 130, winRate: '73.8%', pnl: '+$4,920' },
  { month: 'Nov 2025', trades: 138, winRate: '74.6%', pnl: '+$4,760' },
  { month: 'Dec 2025', trades: 124, winRate: '71.0%', pnl: '+$3,940' },
  { month: 'Jan 2026', trades: 148, winRate: '75.0%', pnl: '+$5,380' },
  { month: 'Feb 2026', trades: 136, winRate: '76.5%', pnl: '+$5,620' },
  { month: 'Mar 2026', trades: 130, winRate: '74.6%', pnl: '+$5,280' },
];


const candles = [
  { o: 392, h: 398, l: 389, c: 396, v: 8 },
  { o: 396, h: 401, l: 393, c: 394, v: 6 },
  { o: 394, h: 399, l: 390, c: 397, v: 7 },
  { o: 397, h: 403, l: 394, c: 400, v: 9 },
  { o: 400, h: 405, l: 396, c: 398, v: 11 },
  { o: 398, h: 400, l: 388, c: 390, v: 13 },
  { o: 390, h: 395, l: 384, c: 386, v: 10 },
  { o: 386, h: 392, l: 383, c: 391, v: 8 },
  { o: 391, h: 394, l: 380, c: 382, v: 14 },
  { o: 382, h: 387, l: 376, c: 378, v: 12 },
  { o: 378, h: 382, l: 370, c: 372, v: 15 },
  { o: 372, h: 376, l: 366, c: 374, v: 9 },
  { o: 374, h: 380, l: 368, c: 370, v: 11 },
  { o: 370, h: 378, l: 367, c: 376, v: 13 },
  { o: 376, h: 388, l: 374, c: 386, v: 16 },
  { o: 386, h: 394, l: 383, c: 392, v: 14 },
  { o: 392, h: 400, l: 389, c: 398, v: 11 },
  { o: 398, h: 406, l: 395, c: 404, v: 12 },
  { o: 404, h: 408, l: 398, c: 400, v: 7 },
  { o: 400, h: 410, l: 397, c: 408, v: 10 },
  { o: 408, h: 416, l: 405, c: 414, v: 13 },
  { o: 414, h: 422, l: 412, c: 420, v: 15 },
  { o: 420, h: 430, l: 418, c: 428, v: 16 },
  { o: 428, h: 436, l: 426, c: 434, v: 14 },
  { o: 434, h: 440, l: 430, c: 432, v: 8 },
  { o: 432, h: 438, l: 428, c: 436, v: 9 },
  { o: 436, h: 442, l: 432, c: 434, v: 7 },
  { o: 434, h: 437, l: 426, c: 428, v: 12 },
  { o: 428, h: 432, l: 422, c: 424, v: 11 },
  { o: 424, h: 430, l: 420, c: 428, v: 8 },
  { o: 428, h: 432, l: 418, c: 420, v: 14 },
  { o: 420, h: 426, l: 414, c: 416, v: 13 },
  { o: 416, h: 420, l: 408, c: 410, v: 10 },
  { o: 410, h: 416, l: 406, c: 414, v: 7 },
  { o: 414, h: 418, l: 404, c: 406, v: 12 },
];
const chartSignals = [
  { idx: 3, type: 'sell', price: '21,400', sl: 408, tp: 380, entry: 400 },
  { idx: 13, type: 'buy', price: '21,376', sl: 364, tp: 408, entry: 376 },
  { idx: 25, type: 'sell', price: '21,436', sl: 444, tp: 414, entry: 436 },
];

const FaqItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'faq-item--open' : ''}`}>
      <button className="faq-item__question" onClick={() => setOpen(!open)}>
        <span>{item.q}</span>
        <ChevronDown size={18} className="faq-item__chevron" />
      </button>
      <div className="faq-item__answer-wrap">
        <p className="faq-item__answer">{item.a}</p>
      </div>
    </div>
  );
};


// Bottom-left interactive particle wave.
// Motion = layered sines + a left-to-right travelling ripple.
// Mouse = soft radial push that decays with distance. Uses rAF so we can
// combine effects smoothly; only 80 transforms per frame (one per column).
const ParticleWave = () => {
  const COLS = 60;
  const ROWS = 7;
  const STEP_X = 12;
  const STEP_Y = 10;
  const VB_W = (COLS - 1) * STEP_X;
  const VB_H = (ROWS - 1) * STEP_Y;

  const svgRef = useRef(null);
  const colsRef = useRef([]);
  // Eased pointer (follows the real cursor with a lag for smoothness) +
  // a strength scalar that fades in/out on enter/leave so the wave never
  // snaps abruptly when the mouse enters or leaves the viewport.
  const mouseRef = useRef({ x: 0, y: 0, strength: 0 });
  const targetRef = useRef({ x: 0, y: 0, strength: 0 });
  const onScreen = useOnScreen(svgRef);

  // Cheap always-on listeners — no rAF work, just state updates on refs.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return undefined;
    const handleMove = (e) => {
      const rect = svg.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * VB_W;
      const y = ((e.clientY - rect.top) / rect.height) * VB_H;
      if (targetRef.current.strength === 0) {
        mouseRef.current.x = x;
        mouseRef.current.y = y;
      }
      targetRef.current = { x, y, strength: 1 };
    };
    const handleLeave = () => {
      targetRef.current.strength = 0;
    };
    window.addEventListener('mousemove', handleMove, { passive: true });
    window.addEventListener('mouseleave', handleLeave);
    document.addEventListener('mouseleave', handleLeave);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseleave', handleLeave);
      document.removeEventListener('mouseleave', handleLeave);
    };
  }, [VB_W, VB_H]);

  // rAF loop — gated on visibility so it pauses the instant the hero
  // scrolls off-screen, and honors prefers-reduced-motion.
  useEffect(() => {
    if (!onScreen) return undefined;
    const mq = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq && mq.matches) return undefined;

    let raf = 0;
    let t = 0;
    const EASE_POS = 0.14;
    const EASE_STRENGTH = 0.08;
    const INFLUENCE = 170;
    const PUSH_Y = 26;
    const PUSH_X = 14;

    const animate = () => {
      t += 0.012;
      mouseRef.current.x += (targetRef.current.x - mouseRef.current.x) * EASE_POS;
      mouseRef.current.y += (targetRef.current.y - mouseRef.current.y) * EASE_POS;
      mouseRef.current.strength +=
        (targetRef.current.strength - mouseRef.current.strength) * EASE_STRENGTH;

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const mStrength = mouseRef.current.strength;

      for (let c = 0; c < COLS; c++) {
        const node = colsRef.current[c];
        if (!node) continue;
        const phase = c * 0.22;
        const base =
          Math.sin(t * 1.1 + phase) * 12 +
          Math.sin(t * 0.55 + phase * 0.4) * 8 +
          Math.sin(t * 2.2 - phase * 0.25) * 3;
        const travel = Math.sin(t * 1.6 - c * 0.12) * 4;
        let y = base + travel;
        let x = 0;

        if (mStrength > 0.01) {
          const colX = c * STEP_X;
          const colY = VB_H * 0.5;
          const dx = colX - mx;
          const dy = colY - my;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < INFLUENCE) {
            const f = 1 - dist / INFLUENCE;
            const eased = f * f * (3 - 2 * f);
            const nx = dist > 0.001 ? dx / dist : 0;
            const ny = dist > 0.001 ? dy / dist : -1;
            x += nx * eased * PUSH_X * mStrength;
            y += ny * eased * PUSH_Y * mStrength;
          }
        }

        node.setAttribute('transform', `translate(${x.toFixed(2)} ${y.toFixed(2)})`);
      }
      raf = requestAnimationFrame(animate);
    };

    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [onScreen, COLS, STEP_X, VB_H]);

  const columns = [];
  for (let c = 0; c < COLS; c++) {
    const dots = [];
    for (let r = 0; r < ROWS; r++) {
      const rowT = r / (ROWS - 1);
      const radius = 0.8 + rowT * 0.9;
      const opacity = 0.16 + rowT * 0.6;
      dots.push(
        <circle
          key={r}
          cx={c * STEP_X}
          cy={r * STEP_Y}
          r={radius}
          fill="#22d3ee"
          opacity={opacity}
        />
      );
    }
    columns.push(
      <g key={c} ref={(el) => { colsRef.current[c] = el; }}>
        {dots}
      </g>
    );
  }

  return (
    <svg
      ref={svgRef}
      className="hero__wave"
      viewBox={`0 -40 ${VB_W} ${VB_H + 80}`}
      preserveAspectRatio="xMinYMax meet"
      aria-hidden="true"
    >
      {columns}
    </svg>
  );
};

const TradingChart = () => {
  const w = 640, h = 360;
  const padL = 44, padR = 16, padT = 20, padB = 48;
  const chartH = h - padT - padB;
  const chartW = w - padL - padR;
  const volH = 28;
  const isMobile = typeof window !== 'undefined' && (window.matchMedia('(hover:none)').matches || window.innerWidth <= 820);

  const rootRef = useRef(null);
  const onScreen = useOnScreen(rootRef);

  // Total draw duration in ms. Must equal 70% of the CSS keyframe
  // cycle durations below (cycle = 7500ms → draw phase = 5250ms).
  const DRAW_MS = 5250;

  const BASE_PRICE = 21327.75;
  const priceRef = useRef(null);
  const changeRef = useRef(null);
  const livePriceRef = useRef(21446.50);

  useEffect(() => {
    if (!onScreen) return undefined;
    let timer;
    const tick = () => {
      const steps = [-2, -1, 0, 1, 1, 2, 3];
      const delta = steps[Math.floor(Math.random() * steps.length)] * 0.25;
      const prev = livePriceRef.current;
      const next = Math.round((prev + delta) * 100) / 100;
      livePriceRef.current = next;
      const el = priceRef.current;
      const chEl = changeRef.current;
      if (el) {
        el.textContent = next.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        el.className = `chart-visual__price chart-visual__price--flash-${next >= prev ? 'up' : 'down'}`;
      }
      if (chEl) {
        const ch = next - BASE_PRICE;
        const pct = (ch / BASE_PRICE) * 100;
        chEl.textContent = `${ch >= 0 ? '+' : ''}${ch.toFixed(2)} (${ch >= 0 ? '+' : ''}${pct.toFixed(2)}%)`;
        chEl.className = `chart-visual__change chart-visual__change--${ch >= 0 ? 'up' : 'down'}`;
      }
      timer = setTimeout(tick, 500 + Math.random() * 700);
    };
    timer = setTimeout(tick, 900);
    return () => clearTimeout(timer);
  }, [onScreen]);

  const initPrice = livePriceRef.current;
  const priceStr = initPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const change = initPrice - BASE_PRICE;
  const changePct = (change / BASE_PRICE) * 100;
  const changeStr = `${change >= 0 ? '+' : ''}${change.toFixed(2)} (${change >= 0 ? '+' : ''}${changePct.toFixed(2)}%)`;

  // Each signal's CSS animation-delay, in ms, positioned to fire when
  // the CSS-animated scanner crosses the signal's candle.
  const signalDelayMs = (sigIdx) =>
    Math.round(((sigIdx + 0.5) / candles.length) * DRAW_MS);

  const allLows = candles.map(c => c.l);
  const allHighs = candles.map(c => c.h);
  const min = Math.min(...allLows) - 8;
  const max = Math.max(...allHighs) + 8;
  const maxVol = Math.max(...candles.map(c => c.v));

  const barW = chartW / candles.length;
  const toY = (v) => padT + chartH - ((v - min) / (max - min)) * chartH;
  const toX = (i) => padL + i * barW + barW / 2;

  // EMA (smoothed close prices)
  const ema = [];
  const k = 2 / (8 + 1);
  candles.forEach((c, i) => {
    ema.push(i === 0 ? c.c : c.c * k + ema[i - 1] * (1 - k));
  });
  const emaPath = ema
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(v).toFixed(1)}`)
    .join(' ');

  const closePath = candles
    .map((c, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(c.c).toFixed(1)}`)
    .join(' ');

  // Price labels for y-axis
  const gridSteps = 6;
  const priceStep = (max - min) / gridSteps;

  return (
    <div className="chart-visual" ref={rootRef}>
      <div className="chart-visual__window">
        <div className="chart-visual__header">
          <div className="chart-visual__header-left">
            <span className="chart-visual__pair">MNQ &middot; 5min</span>
            <span
              ref={priceRef}
              className={`chart-visual__price chart-visual__price--flash-up`}
            >
              {priceStr}
            </span>
            <span ref={changeRef} className={`chart-visual__change chart-visual__change--${change >= 0 ? 'up' : 'down'}`}>
              {changeStr}
            </span>
          </div>
          <span className="chart-visual__live chart-visual__live--demo">
            MNQ 5M
          </span>
        </div>

        <div className="chart-visual__body">
          <svg viewBox={`0 0 ${w} ${h}`} className="chart-visual__svg">
            <defs>
              <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.08" />
                <stop offset="60%" stopColor="#3b82f6" stopOpacity="0.03" />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
              </linearGradient>
              {!isMobile && <filter id="emaGlow">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>}
              <linearGradient id="heroRiskBuy" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor="#ef4444" stopOpacity="0.12" />
                <stop offset="100%" stopColor="#ef4444" stopOpacity="0.03" />
              </linearGradient>
              <linearGradient id="heroRewardBuy" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor="#10b981" stopOpacity="0.03" />
                <stop offset="100%" stopColor="#10b981" stopOpacity="0.12" />
              </linearGradient>
              <linearGradient id="heroRiskSell" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ef4444" stopOpacity="0.12" />
                <stop offset="100%" stopColor="#ef4444" stopOpacity="0.03" />
              </linearGradient>
              <linearGradient id="heroRewardSell" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity="0.03" />
                <stop offset="100%" stopColor="#10b981" stopOpacity="0.12" />
              </linearGradient>
              <linearGradient id="heroScannerGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22d3ee" stopOpacity="0" />
                <stop offset="50%" stopColor="#22d3ee" stopOpacity="0.85" />
                <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
              </linearGradient>
              {!isMobile && <filter id="heroScannerGlow">
                <feGaussianBlur stdDeviation="2" />
              </filter>}
              {/* Cover rect approach replaces clipPath for GPU-composited reveal */}
            </defs>

            {/* Cycle wrapper — a single <g> whose opacity is animated
                on a 6s loop so the cross-cycle reset (clipPath width
                snapping from 580 back to 0 between iterations) is
                invisible to the user. Opacity animations on SVG groups
                are compositor-only, so this costs nothing on scroll. */}
            <g className="chart-visual__cycle">

            {/* Grid lines */}
            {Array.from({ length: gridSteps + 1 }, (_, i) => {
              const pct = i / gridSteps;
              const y = padT + pct * chartH;
              const price = (max - pct * (max - min)).toFixed(0);
              return (
                <g key={i}>
                  <line x1={padL} y1={y} x2={w - padR} y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="0.5" />
                  <text x={padL - 6} y={y + 3} textAnchor="end" fill="rgba(255,255,255,0.18)" fontSize="8" fontFamily="Inter, system-ui, sans-serif">{price}</text>
                </g>
              );
            })}

            {/* Chart content — revealed by the cover rect sliding right */}
            <g>
              {/* Volume bars */}
              {candles.map((c, i) => {
                const x = toX(i);
                const green = c.c >= c.o;
                const volBarH = (c.v / maxVol) * volH;
                return (
                  <rect key={`v${i}`} x={x - barW * 0.28} y={h - padB + 6 + (volH - volBarH)} width={barW * 0.56} height={volBarH} fill={green ? '#10b981' : '#ef4444'} opacity="0.15" rx="0.5" />
                );
              })}

              {/* Area fill */}
              <path d={closePath + ` L${toX(candles.length - 1).toFixed(1)},${(h - padB).toFixed(1)} L${toX(0).toFixed(1)},${(h - padB).toFixed(1)} Z`} fill="url(#areaGrad)" />

              {/* Candles */}
              {candles.map((c, i) => {
                const x = toX(i);
                const green = c.c >= c.o;
                const color = green ? '#10b981' : '#ef4444';
                const bodyTop = toY(Math.max(c.o, c.c));
                const bodyBot = toY(Math.min(c.o, c.c));
                const bodyH = Math.max(bodyBot - bodyTop, 1.2);
                return (
                  <g key={i}>
                    <line x1={x} y1={toY(c.h)} x2={x} y2={toY(c.l)} stroke={color} strokeWidth="0.8" opacity="0.5" />
                    <rect x={x - barW * 0.32} y={bodyTop} width={barW * 0.64} height={bodyH} fill={color} rx="0.8" opacity="0.9" />
                  </g>
                );
              })}

              {/* EMA line with glow */}
              {!isMobile && <path d={emaPath} fill="none" stroke="rgba(34,211,238,0.5)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" filter="url(#emaGlow)" />}
              <path d={emaPath} fill="none" stroke="#22d3ee" strokeWidth={isMobile ? '1.2' : '1.2'} strokeLinecap="round" strokeLinejoin="round" opacity={isMobile ? '0.5' : '0.7'} />

              {/* Risk/Reward zones (revealed by scanner sweep) */}
              {chartSignals.map((s, i) => {
                const isBuy = s.type === 'buy';
                const zoneX = toX(s.idx) - barW / 2;
                const zoneW = barW * 7;
                if (isBuy) {
                  return (
                    <g key={`zone-${i}`}>
                      <rect x={zoneX} y={toY(s.entry)} width={zoneW} height={toY(s.sl) - toY(s.entry)} fill="url(#heroRiskBuy)" />
                      <rect x={zoneX} y={toY(s.tp)} width={zoneW} height={toY(s.entry) - toY(s.tp)} fill="url(#heroRewardBuy)" />
                    </g>
                  );
                }
                return (
                  <g key={`zone-${i}`}>
                    <rect x={zoneX} y={toY(s.sl)} width={zoneW} height={toY(s.entry) - toY(s.sl)} fill="url(#heroRiskSell)" />
                    <rect x={zoneX} y={toY(s.entry)} width={zoneW} height={toY(s.tp) - toY(s.entry)} fill="url(#heroRewardSell)" />
                  </g>
                );
              })}

              {/* Connector dotted lines (revealed by scanner sweep) */}
              {chartSignals.map((s, i) => {
                const c = candles[s.idx];
                const isBuy = s.type === 'buy';
                const cx = toX(s.idx);
                const cy = isBuy ? toY(c.l) + 4 : toY(c.h) - 4;
                const labelY = isBuy ? cy + 6 : cy - 20;
                return (
                  <line
                    key={`conn-${i}`}
                    x1={cx} y1={cy}
                    x2={cx} y2={labelY + (isBuy ? 0 : 14)}
                    stroke={isBuy ? '#10b981' : '#ef4444'}
                    strokeWidth="0.8" strokeDasharray="2 2" opacity="0.5"
                  />
                );
              })}
            </g>

            {/* Cover rect: slides right via translateX (compositor-only) to reveal chart */}
            <rect className="chart-visual__cover" x={padL} y={0} width={chartW + 4} height={h} fill="#0a0f1c" />

            {/* Scanner line sweeps across via GPU-composited transform */}
            <g className="chart-visual__scanner" style={{ transform: `translateX(${padL}px)` }}>
              <rect x={-6} y={padT} width="12" height={chartH} fill="url(#heroScannerGrad)" filter={isMobile ? undefined : 'url(#heroScannerGlow)'} opacity="0.55" />
              <line x1={0} y1={padT} x2={0} y2={padT + chartH} stroke="#22d3ee" strokeWidth="1" opacity="0.9" />
              <circle cx={0} cy={padT + 4} r="2.2" fill="#22d3ee" opacity="0.9" />
            </g>

            {/* Buy/Sell signal badges — each gets a unique keyframe
                with the pop timing baked into percentages (no animation-delay)
                so badges properly reset to hidden at the start of every cycle. */}
            {chartSignals.map((s, i) => {
              const c = candles[s.idx];
              const isBuy = s.type === 'buy';
              const cx = toX(s.idx);
              const cy = isBuy ? toY(c.l) + 4 : toY(c.h) - 4;
              const labelY = isBuy ? cy + 6 : cy - 20;
              const labelCenterY = labelY + 7;
              const delay = signalDelayMs(s.idx);
              const CYCLE = 7500;
              const startPct = (delay / CYCLE * 100).toFixed(2);
              const peakPct = (Math.min(delay + 200, DRAW_MS) / CYCLE * 100).toFixed(2);
              const settlePct = (Math.min(delay + 350, DRAW_MS) / CYCLE * 100).toFixed(2);
              const kfName = `sigPop${i}`;
              return (
                <g key={`sig-${i}`}>
                  <style>{`@keyframes ${kfName}{0%{opacity:0;transform:scale(0)}${startPct}%{opacity:0;transform:scale(0)}${peakPct}%{opacity:1;transform:scale(1.25)}${settlePct}%{opacity:1;transform:scale(1)}90%{opacity:1;transform:scale(1)}95%{opacity:0;transform:scale(0)}100%{opacity:0;transform:scale(0)}}`}</style>
                  <g
                    style={{
                      opacity: 0,
                      animation: `${kfName} ${CYCLE}ms linear infinite`,
                      transformOrigin: `${cx}px ${labelCenterY}px`,
                    }}
                  >
                    <rect x={cx - 24} y={labelY} width="48" height="14" rx="3" fill={isBuy ? '#059669' : '#dc2626'} opacity="0.92" />
                    <text x={cx} y={labelY + 10} textAnchor="middle" fill="#fff" fontSize="7.5" fontWeight="700" fontFamily="Inter, system-ui, sans-serif" letterSpacing="0.02em">
                      {isBuy ? 'BUY' : 'SELL'}
                    </text>
                  </g>
                </g>
              );
            })}

            </g>
          </svg>
        </div>

        <div className="chart-visual__footer">
          <div className="chart-visual__stat">
            <span className="chart-visual__stat-label">Win Rate</span>
            <span className="chart-visual__stat-value chart-visual__stat-value--accent">73.4%</span>
          </div>
          <div className="chart-visual__divider" />
          <div className="chart-visual__stat">
            <span className="chart-visual__stat-label">Today&rsquo;s P/L</span>
            <span className="chart-visual__stat-value chart-visual__stat-value--green">+$485</span>
          </div>
          <div className="chart-visual__divider" />
          <div className="chart-visual__stat">
            <span className="chart-visual__stat-label">Signals</span>
            <span className="chart-visual__stat-value">3</span>
          </div>
          <div className="chart-visual__divider" />
          <div className="chart-visual__stat">
            <span className="chart-visual__stat-label">Contracts</span>
            <span className="chart-visual__stat-value">2 MNQ</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Seeded PRNG for a deterministic, realistic-looking walk (no hydration mismatch)
const mulberry32 = (seed) => {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const EquityCurve = () => {
  const [ref, inView] = useInView();
  const w = 820, h = 220;
  const padL = 48, padR = 16, padT = 16, padB = 28;
  const chartH = h - padT - padB;
  const chartW = w - padL - padR;

  // Build a daily-resolution equity series with realistic drawdowns + retracements
  // while guaranteeing the cumulative total at the end of each month matches
  // the verified monthly totals in monthlyResults.
  const { series, monthEndIndices } = (() => {
    const rng = mulberry32(20260416);
    const tradingDaysPerMonth = 21;
    const series = [];
    const monthEndIndices = [];
    let running = 0;
    series.push(running);

    monthlyResults.forEach((m) => {
      const target = parseInt(String(m.pnl).replace(/[^0-9-]/g, ''), 10) || 0;

      // Generate raw daily deltas with ~73% winners and variable magnitudes.
      // Occasionally inject a bigger loss to create visible drawdowns.
      const raw = [];
      for (let d = 0; d < tradingDaysPerMonth; d++) {
        const u = rng();
        const isWin = u < 0.73;
        if (isWin) {
          // Winning day: log-normal-ish positive
          const mag = 180 + Math.pow(rng(), 1.7) * 820;
          raw.push(mag);
        } else {
          // Losing day: usually moderate, occasionally a larger drawdown spike
          const big = rng() < 0.18;
          const mag = big ? -(380 + rng() * 520) : -(120 + rng() * 360);
          raw.push(mag);
        }
      }

      // Scale so the month's deltas sum exactly to the verified target
      const rawSum = raw.reduce((a, b) => a + b, 0) || 1;
      const scale = target / rawSum;

      for (let d = 0; d < raw.length; d++) {
        running += raw[d] * scale;
        series.push(running);
      }
      monthEndIndices.push(series.length - 1);
    });

    return { series, monthEndIndices };
  })();

  const minVal = Math.min(0, ...series);
  const maxVal = Math.max(...series) * 1.06;
  const stepX = chartW / (series.length - 1);
  const toX = (i) => padL + i * stepX;
  const toY = (v) => padT + chartH - ((v - minVal) / (maxVal - minVal)) * chartH;

  const linePath = series
    .map((v, i) => `${i === 0 ? 'M' : 'L'}${toX(i).toFixed(1)},${toY(v).toFixed(1)}`)
    .join(' ');
  const areaPath = `${linePath} L${toX(series.length - 1).toFixed(1)},${(padT + chartH).toFixed(1)} L${toX(0).toFixed(1)},${(padT + chartH).toFixed(1)} Z`;

  const gridSteps = 4;
  const yLabels = Array.from({ length: gridSteps + 1 }, (_, i) => {
    const val = maxVal - (i / gridSteps) * (maxVal - minVal);
    return { y: padT + (i / gridSteps) * chartH, label: `$${(val / 1000).toFixed(0)}K` };
  });

  const finalValue = Math.round(series[series.length - 1]);

  return (
    <div ref={ref} className={`equity-curve ${inView ? 'equity-curve--visible' : ''}`}>
      <div className="equity-curve__header">
        <div>
          <span className="equity-curve__title">Cumulative P/L &middot; last 10 months</span>
          <span className="equity-curve__subtitle">Every verified trade, compounded daily</span>
        </div>
        <div className="equity-curve__total">
          <span className="equity-curve__total-value">+${finalValue.toLocaleString()}</span>
          <span className="equity-curve__total-label">Realized</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="equity-curve__svg" preserveAspectRatio="none">
        <defs>
          <linearGradient id="eqArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#34d399" stopOpacity="0.32" />
            <stop offset="100%" stopColor="#34d399" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="eqLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="100%" stopColor="#34d399" />
          </linearGradient>
          <filter id="eqGlow">
            <feGaussianBlur stdDeviation="1.6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {yLabels.map((g, i) => (
          <g key={i}>
            <line x1={padL} y1={g.y} x2={w - padR} y2={g.y} stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
            <text x={padL - 8} y={g.y + 3} textAnchor="end" fill="rgba(255,255,255,0.3)" fontSize="10" fontFamily="Inter, system-ui, sans-serif" fontWeight="500">{g.label}</text>
          </g>
        ))}

        <path className="equity-curve__area" d={areaPath} fill="url(#eqArea)" />
        <path className="equity-curve__line" d={linePath} fill="none" stroke="url(#eqLine)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" filter="url(#eqGlow)" />

        {monthEndIndices.map((endIdx, i) => (
          <g key={i}>
            <circle
              className="equity-curve__dot"
              cx={toX(endIdx)}
              cy={toY(series[endIdx])}
              r="3.2"
              fill="#0a1628"
              stroke="#34d399"
              strokeWidth="1.6"
              style={{ '--dot-i': i }}
            />
            <text
              x={toX(endIdx)}
              y={h - 10}
              textAnchor="middle"
              fill="rgba(255,255,255,0.35)"
              fontSize="9.5"
              fontFamily="Inter, system-ui, sans-serif"
              fontWeight="500"
            >
              {monthlyResults[i].month.split(' ')[0]}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

// ---- Live NY-session helpers ----------------------------------------
// Extracted so both the status pill and the NOW marker on the chart
// can share the same derived values without duplicating Intl logic.

const getEtParts = (date) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric', minute: 'numeric', second: 'numeric',
      hour12: false, weekday: 'short',
    }).formatToParts(date).reduce((a, p) => ({ ...a, [p.type]: p.value }), {});
    return {
      h: parseInt(parts.hour, 10) % 24,
      m: parseInt(parts.minute, 10),
      s: parseInt(parts.second, 10),
      wd: parts.weekday,
      valid: true,
    };
  } catch {
    return { h: 0, m: 0, s: 0, wd: 'Mon', valid: false };
  }
};

const computeNyStatus = (date) => {
  const et = getEtParts(date);
  if (!et.valid) {
    return { open: true, label: 'Live during NY hours', et };
  }
  const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
  const isWeekday = WEEKDAYS.includes(et.wd);
  const mins = et.h * 60 + et.m;
  const openMin = 9 * 60 + 30;
  const closeMin = 16 * 60;

  if (isWeekday && mins >= openMin && mins < closeMin) {
    const secsLeft = (closeMin - mins) * 60 - et.s;
    const h = Math.floor(secsLeft / 3600);
    const m = Math.floor((secsLeft % 3600) / 60);
    const s = secsLeft % 60;
    return {
      open: true,
      label: `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s left`,
      et,
    };
  }

  // Closed — compute seconds until next 9:30 ET open.
  let daysAhead;
  if (isWeekday) {
    if (mins < openMin) daysAhead = 0;
    else if (et.wd === 'Fri') daysAhead = 3;
    else daysAhead = 1;
  } else if (et.wd === 'Sat') {
    daysAhead = 2;
  } else {
    daysAhead = 1;
  }
  const currentSecs = et.h * 3600 + et.m * 60 + et.s;
  const openSecs = openMin * 60;
  let secsUntil = daysAhead * 86400 + openSecs - currentSecs;
  if (secsUntil < 0) secsUntil += 86400;

  const d = Math.floor(secsUntil / 86400);
  const h = Math.floor((secsUntil % 86400) / 3600);
  const m = Math.floor((secsUntil % 3600) / 60);
  const s = secsUntil % 60;

  const label = d > 0
    ? `Opens in ${d}d ${h}h ${String(m).padStart(2, '0')}m`
    : `Opens in ${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;

  return { open: false, label, et };
};

// Live status pill — ticks every second; only this tiny component re-
// renders, so the rest of the sessions viz stays static.
const LiveNyStatus = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const status = computeNyStatus(now);
  return (
    <div className={`sessions__status sessions__status--${status.open ? 'open' : 'closed'}`}>
      <span className="sessions__status-dot" />
      <span className="sessions__status-label">{status.open ? 'NY OPEN' : 'NY CLOSED'}</span>
      <span className="sessions__status-time">{status.label}</span>
    </div>
  );
};

// "NOW" vertical marker inside the 24-hour volume chart. Position is a
// function of current ET time; updates every 30s (the chart is 24h
// wide, so faster ticks are imperceptible). Lives in its own component
// so the whole sessions section doesn't re-render every tick.
const LiveNowMarker = ({ padL, padT, chartH, chartW, hoursCount = 24 }) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  const status = computeNyStatus(now);
  const { et, open } = status;
  if (!et.valid) return null;

  const nowHour = et.h + et.m / 60;
  const x = padL + nowHour * (chartW / hoursCount);
  const color = open ? '#34d399' : '#22d3ee';
  const hourDisplay = et.h % 12 || 12;
  const ampm = et.h < 12 ? 'a' : 'p';
  const timeLabel = `${hourDisplay}:${String(et.m).padStart(2, '0')}${ampm}`;
  // Keep the label box fully inside the chart even at extremes.
  const labelX = Math.min(Math.max(x, padL + 28), padL + chartW - 28);

  return (
    <g className="sessions__viz-now">
      <line
        x1={x} y1={padT}
        x2={x} y2={padT + chartH}
        stroke={color}
        strokeWidth="1"
        strokeDasharray="3 3"
        opacity="0.5"
      />
      <circle cx={x} cy={padT + chartH} r="2.8" fill={color} />
      <g transform={`translate(${labelX}, ${padT - 2})`}>
        <rect
          x="-30" y="-13"
          width="60" height="13"
          rx="3"
          fill="#0a0f1c"
          stroke={color}
          strokeWidth="0.8"
          opacity="0.95"
        />
        <text
          x="0" y="-3.5"
          textAnchor="middle"
          fill={color}
          fontSize="7.5"
          fontWeight="700"
          letterSpacing="0.1em"
          fontFamily="Inter, system-ui, sans-serif"
        >
          NOW · {timeLabel}
        </text>
      </g>
    </g>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { isLoggedIn, loading: authLoading } = useAuthContext();

  useEffect(() => {
    if (typeof window.fbq === 'function') window.fbq('track', 'ViewContent', { content_name: 'Landing Page' });

    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(k => {
      if (params.get(k)) utm[k] = params.get(k);
    });
    if (Object.keys(utm).length > 0) {
      sessionStorage.setItem('anq_utm', JSON.stringify(utm));
    }

    // Inject the exact waitlist vanilla JS chart on mobile
    if (window.innerWidth <= 768) {
      const mount = document.getElementById('mobileChartMount');
      if (!mount) return;

      const cndl = [
        {o:392,h:398,l:389,c:396},{o:396,h:401,l:393,c:394},{o:394,h:399,l:390,c:397},
        {o:397,h:403,l:394,c:400},{o:400,h:405,l:396,c:398},{o:398,h:400,l:388,c:390},
        {o:390,h:395,l:384,c:386},{o:386,h:392,l:383,c:391},{o:391,h:394,l:380,c:382},
        {o:382,h:387,l:376,c:378},{o:378,h:382,l:370,c:372},{o:372,h:376,l:366,c:374},
        {o:374,h:380,l:368,c:370},{o:370,h:378,l:367,c:376},{o:376,h:388,l:374,c:386},
        {o:386,h:394,l:383,c:392},{o:392,h:400,l:389,c:398},{o:398,h:406,l:395,c:404},
        {o:404,h:408,l:398,c:400},{o:400,h:410,l:397,c:408},{o:408,h:416,l:405,c:414},
        {o:414,h:422,l:412,c:420},{o:420,h:430,l:418,c:428},{o:428,h:436,l:426,c:434},
        {o:434,h:440,l:430,c:432},{o:432,h:438,l:428,c:436},{o:436,h:442,l:432,c:434},
        {o:434,h:437,l:426,c:428},{o:428,h:432,l:422,c:424},{o:424,h:430,l:420,c:428},
        {o:428,h:432,l:418,c:420},{o:420,h:426,l:414,c:416},{o:416,h:420,l:408,c:410},
        {o:410,h:416,l:406,c:414}
      ];
      const sigs = [
        {idx:3,type:'sell',entry:400,sl:408,tp:380},
        {idx:13,type:'buy',entry:376,sl:364,tp:408},
        {idx:25,type:'sell',entry:436,sl:444,tp:414}
      ];
      const W=520,H=300,PL=34,PT=20,PB=28,CW=W-PL,CH=H-PT-PB;
      const allV=cndl.map(c=>c.l).concat(cndl.map(c=>c.h));
      allV.push(364,444);
      const mn=Math.min(...allV)-6, mx=Math.max(...allV)+6;
      const bW=CW/cndl.length, DMS=5250, CYC=7500;
      const tX=i=>PL+i*bW+bW/2;
      const tY=v=>PT+CH-((v-mn)/(mx-mn))*CH;
      const em=[];const ek=2/9;
      cndl.forEach((c,i)=>{em.push(i===0?c.c:c.c*ek+em[i-1]*(1-ek))});
      const emP=em.map((v,i)=>(i===0?'M':'L')+tX(i).toFixed(1)+','+tY(v).toFixed(1)).join(' ');
      const clP=cndl.map((c,i)=>(i===0?'M':'L')+tX(i).toFixed(1)+','+tY(c.c).toFixed(1)).join(' ');

      let st='<style>';
      sigs.forEach((s,i)=>{
        const d=((s.idx+0.5)/cndl.length)*DMS;
        const s1=(d/CYC*100).toFixed(2),s2=(Math.min(d+200,DMS)/CYC*100).toFixed(2),s3=(Math.min(d+350,DMS)/CYC*100).toFixed(2);
        st+=`@keyframes msp${i}{0%{opacity:0;transform:scale(0)}${s1}%{opacity:0;transform:scale(0)}${s2}%{opacity:1;transform:scale(1.3)}${s3}%{opacity:1;transform:scale(1)}90%{opacity:1;transform:scale(1)}95%{opacity:0;transform:scale(0)}100%{opacity:0;transform:scale(0)}}`;
      });
      st+='</style>';

      let s=st+`<div style="display:flex;align-items:center;justify-content:space-between;padding:6px 10px;background:rgba(255,255,255,0.02);border-bottom:1px solid rgba(255,255,255,0.04)"><div><span style="font-size:10px;font-weight:700;color:#fff">MNQ · 5min</span><span id="mcp" style="font-size:10px;font-weight:600;color:#22d3ee;margin-left:8px">21,446.50</span></div><span style="font-size:8px;font-weight:600;color:rgba(255,255,255,0.3);text-transform:uppercase;letter-spacing:0.05em">MNQ 5M</span></div>`;
      s+='<svg viewBox="0 0 '+W+' '+H+'" style="display:block;width:100%"><defs>';
      s+='<linearGradient id="mag" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#22d3ee" stop-opacity="0.08"/><stop offset="100%" stop-color="#22d3ee" stop-opacity="0"/></linearGradient>';
      s+='<linearGradient id="msg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#22d3ee" stop-opacity="0"/><stop offset="50%" stop-color="#22d3ee" stop-opacity="0.85"/><stop offset="100%" stop-color="#22d3ee" stop-opacity="0"/></linearGradient>';
      s+='<linearGradient id="mrb" x1="0" y1="1" x2="0" y2="0"><stop offset="0%" stop-color="#ef4444" stop-opacity="0.14"/><stop offset="100%" stop-color="#ef4444" stop-opacity="0.02"/></linearGradient>';
      s+='<linearGradient id="mwb" x1="0" y1="1" x2="0" y2="0"><stop offset="0%" stop-color="#10b981" stop-opacity="0.02"/><stop offset="100%" stop-color="#10b981" stop-opacity="0.14"/></linearGradient>';
      s+='<linearGradient id="mrs" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#ef4444" stop-opacity="0.14"/><stop offset="100%" stop-color="#ef4444" stop-opacity="0.02"/></linearGradient>';
      s+='<linearGradient id="mws" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#10b981" stop-opacity="0.02"/><stop offset="100%" stop-color="#10b981" stop-opacity="0.14"/></linearGradient>';
      s+='</defs><g class="mcv__cycle">';
      for(let g=0;g<=5;g++){const p=g/5,gy=PT+p*CH,pr=(mx-p*(mx-mn)).toFixed(0);s+=`<line x1="${PL}" y1="${gy.toFixed(1)}" x2="${W}" y2="${gy.toFixed(1)}" stroke="rgba(255,255,255,0.04)" stroke-width="0.5"/><text x="${PL-4}" y="${(gy+3).toFixed(1)}" text-anchor="end" fill="rgba(255,255,255,0.15)" font-size="6.5" font-family="Inter,sans-serif">${pr}</text>`}
      s+='<g>';
      sigs.forEach(sg=>{const ib=sg.type==='buy',zx=tX(sg.idx)-bW/2,zw=bW*7;if(ib){s+=`<rect x="${zx.toFixed(1)}" y="${tY(sg.entry).toFixed(1)}" width="${zw.toFixed(1)}" height="${(tY(sg.sl)-tY(sg.entry)).toFixed(1)}" fill="url(#mrb)"/><rect x="${zx.toFixed(1)}" y="${tY(sg.tp).toFixed(1)}" width="${zw.toFixed(1)}" height="${(tY(sg.entry)-tY(sg.tp)).toFixed(1)}" fill="url(#mwb)"/>`}else{s+=`<rect x="${zx.toFixed(1)}" y="${tY(sg.sl).toFixed(1)}" width="${zw.toFixed(1)}" height="${(tY(sg.entry)-tY(sg.sl)).toFixed(1)}" fill="url(#mrs)"/><rect x="${zx.toFixed(1)}" y="${tY(sg.entry).toFixed(1)}" width="${zw.toFixed(1)}" height="${(tY(sg.tp)-tY(sg.entry)).toFixed(1)}" fill="url(#mws)"/>`}s+=`<line x1="${zx.toFixed(1)}" y1="${tY(sg.entry).toFixed(1)}" x2="${(zx+zw).toFixed(1)}" y2="${tY(sg.entry).toFixed(1)}" stroke="rgba(255,255,255,0.12)" stroke-width="0.5" stroke-dasharray="2 2"/>`});
      const la=cndl.length-1;
      s+=`<path d="${clP} L${tX(la).toFixed(1)},${(H-PB).toFixed(1)} L${tX(0).toFixed(1)},${(H-PB).toFixed(1)} Z" fill="url(#mag)"/>`;
      cndl.forEach((c,i)=>{const x=tX(i),gr=c.c>=c.o,co=gr?'#10b981':'#ef4444',bt=tY(Math.max(c.o,c.c)),bb=tY(Math.min(c.o,c.c)),bh=Math.max(bb-bt,1.2);s+=`<line x1="${x.toFixed(1)}" y1="${tY(c.h).toFixed(1)}" x2="${x.toFixed(1)}" y2="${tY(c.l).toFixed(1)}" stroke="${co}" stroke-width="0.7" opacity="0.5"/><rect x="${(x-bW*0.32).toFixed(1)}" y="${bt.toFixed(1)}" width="${(bW*0.64).toFixed(1)}" height="${bh.toFixed(1)}" fill="${co}" rx="0.6" opacity="0.88"/>`});
      s+=`<path d="${emP}" fill="none" stroke="#22d3ee" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" opacity="0.5"/>`;
      s+='</g>';
      s+=`<rect class="mcv__cover" x="${PL}" y="0" width="${CW+4}" height="${H}" fill="#000"/>`;
      s+=`<g class="mcv__scanner" style="transform:translateX(${PL}px)"><rect x="-4" y="${PT}" width="8" height="${CH}" fill="url(#msg)" opacity="0.5"/><line x1="0" y1="${PT}" x2="0" y2="${PT+CH}" stroke="#22d3ee" stroke-width="0.8" opacity="0.8"/><circle cx="0" cy="${PT+3}" r="1.5" fill="#22d3ee" opacity="0.9"/></g>`;
      sigs.forEach((sg,i)=>{const ib=sg.type==='buy',c=cndl[sg.idx],cx=tX(sg.idx),cy=ib?tY(c.l)+4:tY(c.h)-4,ly=ib?cy+5:cy-17,co=ib?'#059669':'#dc2626',lb=ib?'BUY':'SELL';s+=`<g style="opacity:0;animation:msp${i} ${CYC}ms linear infinite;transform-origin:${cx.toFixed(1)}px ${(ly+6).toFixed(1)}px"><rect x="${(cx-22).toFixed(1)}" y="${ly.toFixed(1)}" width="44" height="12" rx="2.5" fill="${co}" opacity="0.92"/><text x="${cx.toFixed(1)}" y="${(ly+8.5).toFixed(1)}" text-anchor="middle" fill="#fff" font-size="7" font-weight="700" font-family="Inter,sans-serif">${lb}</text></g>`});
      s+='</g></svg>';
      mount.innerHTML = s;

      let pr = 21446.50;
      const pel = document.getElementById('mcp');
      const iv = setInterval(() => {
        const steps = [-2,-1,0,1,1,2,3];
        pr += steps[Math.floor(Math.random()*steps.length)] * 0.25;
        pr = Math.round(pr * 100) / 100;
        if (pel) {
          pel.textContent = pr.toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});
          pel.style.color = pr >= 21446.50 ? '#22d3ee' : '#ef4444';
        }
      }, 600);
      return () => clearInterval(iv);
    }
  }, []);

  const handleGetStarted = () => {
    if (isLoggedIn) { navigate('/dashboard'); return; }
    navigate('/signup');
  };

  // Stat counters animate when the Performance section enters view.
  const [perfRef, perfInView] = useInView({ threshold: 0.3 });
  const tradesCount = useCountUp(1352, 1500, perfInView);
  const winRateCount = useCountUp(73.4, 1500, perfInView);
  const profitCount = useCountUp(47320, 1700, perfInView);

  // Pause all hero chart animations whenever the hero is scrolled out
  // of view. The animations loop forever on a 6s cycle, so pausing
  // offscreen keeps the compositor idle when the user isn't looking —
  // and on scroll-back the animation resumes exactly where it was and
  // finishes its current cycle naturally. No abrupt snaps, no wasted
  // GPU frames on an invisible element.
  const heroRef = useRef(null);
  const [heroVisible, setHeroVisible] = useState(true);
  useEffect(() => {
    const el = heroRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => setHeroVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="landing">
      {/* Nav */}
      <nav className="lnav">
        <div className="lnav__inner">
          <div className="lnav__logo">
            <img src="/logo.png" alt="AlphaNQ" className="lnav__logo-img" />
          </div>
          <div className="lnav__actions">
            <button className="lnav__link" onClick={() => navigate(isLoggedIn ? '/dashboard' : '/login')}>
              <LogIn size={16} />
              {isLoggedIn ? 'Dashboard' : 'Sign In'}
            </button>
            <button className="lnav__cta" onClick={handleGetStarted}>
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className={`hero ${!heroVisible ? 'hero--paused' : ''}`} ref={heroRef}>
        <div className="hero__grid-bg" />
        <div className="hero__beams" aria-hidden="true">
          <span className="hero__beam hero__beam--1" />
          <span className="hero__beam hero__beam--2" />
          <span className="hero__beam hero__beam--3" />
        </div>
        <div className="hero__glow hero__glow--1" />
        <div className="hero__glow hero__glow--2" />
        {typeof window !== 'undefined' && window.innerWidth > 768 && <ParticleWave />}
        <div className="hero__inner">
          <div className="hero__text">
            <div className="hero__badge">
              <span className="hero__badge-dot" />
              <span>For MNQ traders</span>
              <span className="hero__badge-sep" aria-hidden="true" />
              <span>5-min Nasdaq</span>
              <span className="hero__badge-sep" aria-hidden="true" />
              <span>NY session only</span>
            </div>
            <h1 className="hero__title">
              The ML edge on<br />
              <span className="hero__title-accent">5min NQ Futures.</span>
            </h1>
            <span className="hero__title-bar" aria-hidden="true" />
            <p className="hero__desc">
              ML-powered signals. Defined risk.<br />
              Real-time edge for the NY session.
            </p>
            <div className="hero__cta-group">
              <div className="hero__cta-row">
                <button className="hero__cta" onClick={handleGetStarted}>
                  Get today&rsquo;s signals
                  <ArrowRight size={17} />
                </button>
                <button
                  className="hero__cta-ghost"
                  onClick={() => {
                    const el = document.querySelector('.performance');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  View verified track record
                </button>
              </div>
              <span className="hero__cta-sub">
                <ShieldCheck size={13} />
                7-day free trial
              </span>
            </div>
            <div className="hero__proof">
              <div className="hero__proof-item">
                <span className="hero__proof-icon hero__proof-icon--accent">
                  <TrendingUp size={16} />
                </span>
                <span className="hero__proof-value hero__proof-value--accent">73.4%</span>
                <span className="hero__proof-label">Win rate</span>
              </div>
              <div className="hero__proof-item">
                <span className="hero__proof-icon">
                  <ShieldCheck size={16} />
                </span>
                <span className="hero__proof-value">1,352</span>
                <span className="hero__proof-label">Verified trades</span>
              </div>
              <div className="hero__proof-item">
                <span className="hero__proof-icon hero__proof-icon--green">
                  <DollarSign size={16} />
                </span>
                <span className="hero__proof-value">$47K+</span>
                <span className="hero__proof-label">Total profit</span>
              </div>
            </div>
            <p className="hero__verified-note">
              <Lock size={11} />
              Verified by track record. 100% transparent.
            </p>
          </div>
          <div className="hero__visual">
            <div className="hero__monitor">
              <div className="hero__monitor-bezel">
                <div className="hero__monitor-screen">
                  <div className="hero__chart-desktop"><TradingChart /></div>
                  <div className="hero__chart-mobile" id="mobileChartMount" />
                </div>
                <div className="hero__monitor-chin">
                  <span className="hero__monitor-logo">AlphaNQ</span>
                </div>
              </div>
              <div className="hero__monitor-neck" aria-hidden="true" />
              <div className="hero__monitor-stand" aria-hidden="true" />
              <div className="hero__monitor-base" aria-hidden="true" />
              <div className="hero__monitor-floor" aria-hidden="true" />
            </div>
          </div>
        </div>
      </section>

      {/* Performance — Right after hero for maximum impact */}
      <section className="performance" ref={perfRef}>
        <div className="performance__grid-bg" />
        <div className="performance__glow" />
        <div className="performance__glow performance__glow--2" />
        <div className="performance__inner">
          <div className="performance__badge">
            <ShieldCheck size={14} />
            Verified track record
          </div>
          <h2 className="performance__heading">10 months of verified results</h2>
          <p className="performance__sub">Every trade logged. Every result public. No exceptions.</p>

          <div className="performance__stats-row">
            <div className="performance__stat-card">
              <span className="performance__stat-value performance__stat-value--accent">
                {Math.round(tradesCount).toLocaleString()}
              </span>
              <span className="performance__stat-label">Total Trades</span>
            </div>
            <div className="performance__stat-card">
              <span className="performance__stat-value performance__stat-value--green">
                {winRateCount.toFixed(1)}%
              </span>
              <span className="performance__stat-label">Win Rate</span>
            </div>
            <div className="performance__stat-card">
              <span className="performance__stat-value performance__stat-value--green">
                +${Math.round(profitCount).toLocaleString()}
              </span>
              <span className="performance__stat-label">Total Profit</span>
            </div>
          </div>

          <EquityCurve />

          <div className="performance__table-wrap">
            <table className="performance__table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Trades</th>
                  <th>Win Rate</th>
                  <th>Net P/L</th>
                </tr>
              </thead>
              <tbody>
                {monthlyResults.map((r, i) => (
                  <tr key={i} className="performance__row" style={{'--row-delay': `${i * 0.06}s`}}>
                    <td className="performance__month">{r.month}</td>
                    <td>{r.trades}</td>
                    <td className="performance__winrate">{r.winRate}</td>
                    <td className="performance__pnl">{r.pnl}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="performance__footer">
            <p className="performance__note">
              Based on Tier 5 position sizing ($5,000 account). Results vary by tier and market conditions.
              Last updated {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.
            </p>
            <button className="performance__download" type="button">
              <Database size={13} />
              Download full trade log (CSV)
            </button>
          </div>
        </div>
      </section>

      {/* 5-Minute Chart Differentiator */}
      {(() => {
        const [fiveMinRef, fiveMinInView] = useInView();
        return (
          <section className={`fivemin ${fiveMinInView ? 'fivemin--visible' : ''}`} ref={fiveMinRef}>
            <div className="fivemin__glow" />
            <div className="fivemin__glow fivemin__glow--2" />

            {/* Animated background grid */}
            <div className="fivemin__grid-bg" />

            {/* Floating particles */}
            <div className="fivemin__particles">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="fivemin__particle" style={{
                  '--p-x': `${10 + Math.random() * 80}%`,
                  '--p-y': `${10 + Math.random() * 80}%`,
                  '--p-delay': `${i * 0.6}s`,
                  '--p-dur': `${4 + Math.random() * 3}s`,
                  '--p-size': `${2 + Math.random() * 2}px`,
                }} />
              ))}
            </div>

            <div className="fivemin__inner">
              <div className="fivemin__badge">
                <Zap size={14} />
                Purpose-built algorithm
              </div>
              <h2 className="fivemin__title">
                One timeframe.<br />
                <span className="fivemin__title-accent">Precision-engineered.</span>
              </h2>
              <p className="fivemin__subtitle">
                Generic indicators try to work on everything. Ours was built for one chart.
              </p>

              <div className="fivemin__compare">
                <div className="fivemin__compare-card fivemin__compare-card--generic">
                  <div className="fivemin__compare-fade" />
                  <div className="fivemin__compare-header">
                    <span className="fivemin__compare-icon-wrap fivemin__compare-icon-wrap--red">
                      <X size={14} />
                    </span>
                    <span className="fivemin__compare-label">Generic Indicators</span>
                  </div>
                  <ul className="fivemin__compare-list">
                    {[
                      { title: 'Built for any timeframe, any market', sub: 'Optimized for nothing in particular' },
                      { title: 'One-size-fits-all parameters', sub: 'Static settings, zero personalization' },
                      { title: 'No ML or statistical backtesting', sub: 'Hand-picked rules, never validated' },
                      { title: 'Conflicting, overlapping signals', sub: 'Analysis paralysis on every bar' },
                    ].map(({ title, sub }, i) => (
                      <li key={i} style={{'--li-i': i}}>
                        <span className="fivemin__x-icon"><X size={12} /></span>
                        <span className="fivemin__compare-item">
                          <span className="fivemin__compare-item-title">{title}</span>
                          <span className="fivemin__compare-item-sub">{sub}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="fivemin__compare-foot fivemin__compare-foot--red">
                    Edge: <span>unmeasured</span>
                  </div>
                </div>

                <div className="fivemin__vs">
                  <div className="fivemin__vs-glow" />
                  <span className="fivemin__vs-text">VS</span>
                </div>

                <div className="fivemin__compare-card fivemin__compare-card--alpha">
                  <div className="fivemin__compare-glow" />
                  <div className="fivemin__compare-scan" />
                  <div className="fivemin__alpha-border" />
                  <div className="fivemin__compare-header">
                    <span className="fivemin__compare-icon-wrap fivemin__compare-icon-wrap--green">
                      <CheckCircle size={14} />
                    </span>
                    <span className="fivemin__compare-label">AlphaNQ</span>
                  </div>
                  <ul className="fivemin__compare-list">
                    {[
                      { title: '5-min Nasdaq futures only', sub: 'One chart. One instrument. Zero compromises.' },
                      { title: '5 years of backtested training', sub: '98,280 bars analyzed, every regime' },
                      { title: 'Tuned for the NY session', sub: '9:30 – 4:00 ET, where 70% of volume lives' },
                      { title: 'One clean signal at a time', sub: 'Entry, stop, target — no ambiguity' },
                    ].map(({ title, sub }, i) => (
                      <li key={i} style={{'--li-i': i}}>
                        <span className="fivemin__check-icon"><Check size={12} /></span>
                        <span className="fivemin__compare-item">
                          <span className="fivemin__compare-item-title">{title}</span>
                          <span className="fivemin__compare-item-sub">{sub}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="fivemin__compare-foot fivemin__compare-foot--green">
                    Edge: <span>73.4% win rate · verified</span>
                  </div>
                </div>
              </div>

            </div>
          </section>
        );
      })()}

      {/* New York Session — 24hr volume visualization */}
      {(() => {
        const [sessionsRef, sessionsInView] = useInView();

        // Relative trading volume across a 24-hour ET day (hours 0-23)
        // Classic profile: Asian overnight flat, London ramp, NY spike, taper
        const volumeByHour = [
          18, 22, 24, 28, 32, 38, 42, 46, 55, 72,
          94, 88, 62, 58, 66, 82, 76, 38, 26, 22,
          20, 18, 16, 19,
        ];

        // Session windows in ET (24h)
        const sessions = [
          { name: 'Asian',    start: 18, end: 24, tone: 'asian' },
          { name: 'Asian',    start: 0,  end: 3,  tone: 'asian', noLabel: true },
          { name: 'London',   start: 3,  end: 9.5, tone: 'london' },
          { name: 'New York', start: 9.5, end: 16, tone: 'ny' },
        ];

        // Live NY status (open + countdown) is rendered by <LiveNyStatus />
        // inside the JSX below, so it updates every second without re-
        // rendering the rest of the section. Same for <LiveNowMarker />
        // which draws the "you are here" vertical line on the chart.

        // SVG dims
        const W = 560, H = 230;
        const padL = 36, padR = 16, padT = 20, padB = 58;
        const chartW = W - padL - padR;
        const chartH = H - padT - padB;
        const hoursCount = 24;
        const barW = chartW / hoursCount;
        const maxVol = Math.max(...volumeByHour);
        const toX = (hr) => padL + hr * (chartW / hoursCount);
        const toY = (v) => padT + chartH - (v / maxVol) * chartH;

        return (
          <section className={`sessions ${sessionsInView ? 'sessions--visible' : ''}`} ref={sessionsRef}>
            <div className="sessions__grid-bg" />
            <div className="sessions__glow sessions__glow--1" />
            <div className="sessions__glow sessions__glow--2" />

            <div className="sessions__inner">
              <div className="sessions__text">
                <div className="sessions__badge">
                  <Radio size={14} />
                  The trading window
                </div>
                <h2 className="sessions__title">
                  Where the money<br />
                  <span className="sessions__title-accent">actually moves.</span>
                </h2>
                <p className="sessions__subtitle">
                  The 24-hour Nasdaq isn&rsquo;t created equal. Over <strong>70% of daily volume</strong> fires
                  in a single 6.5-hour window. That&rsquo;s the only window we trade.
                </p>
                <div className="sessions__highlights">
                  <div className="sessions__highlight">
                    <Check size={14} />
                    <span>Deepest liquidity &middot; tightest spreads</span>
                  </div>
                  <div className="sessions__highlight">
                    <Check size={14} />
                    <span>Cleanest price action the algorithm can read</span>
                  </div>
                  <div className="sessions__highlight">
                    <Check size={14} />
                    <span>Zero overnight gambles &middot; flat by 4:00 PM ET</span>
                  </div>
                </div>
              </div>

              <div className="sessions__viz">
                <div className="sessions__viz-card">
                  <div className="sessions__viz-border" />
                  <div className="sessions__viz-glow" />

                  <div className="sessions__viz-header">
                    <div className="sessions__viz-header-left">
                      <span className="sessions__viz-title">Nasdaq 24-hour volume</span>
                      <span className="sessions__viz-subtitle">Relative volume, ET &middot; typical weekday</span>
                    </div>
                    <LiveNyStatus />
                  </div>

                  <svg viewBox={`0 0 ${W} ${H}`} className="sessions__viz-svg" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="sessNyBar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#5eead4" stopOpacity="1" />
                        <stop offset="60%" stopColor="#34d399" stopOpacity="0.95" />
                        <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.45" />
                      </linearGradient>
                      <linearGradient id="sessLondonBar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#818cf8" stopOpacity="0.65" />
                        <stop offset="100%" stopColor="#60a5fa" stopOpacity="0.2" />
                      </linearGradient>
                      <linearGradient id="sessAsianBar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgba(255,255,255,0.25)" />
                        <stop offset="100%" stopColor="rgba(255,255,255,0.06)" />
                      </linearGradient>
                      <radialGradient id="sessNyZone" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="rgba(52, 211, 153, 0.14)" />
                        <stop offset="70%" stopColor="rgba(52, 211, 153, 0.04)" />
                        <stop offset="100%" stopColor="rgba(52, 211, 153, 0)" />
                      </radialGradient>
                      <filter id="sessNyGlow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="1.8" result="blur" />
                        <feMerge>
                          <feMergeNode in="blur" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                    </defs>

                    {/* Subtle session zone tints — helps the 4-session
                        narrative (Asian / London / NY / Asian) read at a
                        glance. Pure fill-gradient rects, paint-once. */}
                    <rect
                      x={toX(0)} y={padT}
                      width={toX(3) - toX(0)} height={chartH}
                      fill="rgba(255,255,255,0.018)"
                    />
                    <rect
                      x={toX(3)} y={padT}
                      width={toX(9.5) - toX(3)} height={chartH}
                      fill="rgba(129,140,248,0.05)"
                    />
                    <rect
                      x={toX(16)} y={padT}
                      width={toX(18) - toX(16)} height={chartH}
                      fill="rgba(255,255,255,0.018)"
                    />
                    <rect
                      x={toX(18)} y={padT}
                      width={toX(24) - toX(18)} height={chartH}
                      fill="rgba(255,255,255,0.018)"
                    />

                    {/* NY zone soft background glow */}
                    <ellipse
                      cx={(toX(9.5) + toX(16)) / 2}
                      cy={padT + chartH * 0.55}
                      rx={(toX(16) - toX(9.5)) / 2 + 10}
                      ry={chartH * 0.6}
                      fill="url(#sessNyZone)"
                      className="sessions__viz-ny-zone"
                    />

                    {/* Subtle baseline */}
                    <line
                      x1={padL}
                      y1={padT + chartH + 2}
                      x2={W - padR}
                      y2={padT + chartH + 2}
                      stroke="rgba(255,255,255,0.05)"
                      strokeWidth="1"
                    />

                    {/* Bars */}
                    {volumeByHour.map((v, hr) => {
                      const isNy = hr >= 9 && hr < 16;
                      const isLondon = hr >= 3 && hr < 9;
                      const fill = isNy ? 'url(#sessNyBar)' : isLondon ? 'url(#sessLondonBar)' : 'url(#sessAsianBar)';
                      const x = toX(hr) + barW * 0.18;
                      const bW = barW * 0.64;
                      const barH = chartH - (toY(v) - padT);
                      const y = toY(v);
                      return (
                        <rect
                          key={hr}
                          x={x}
                          y={y}
                          width={bW}
                          height={barH}
                          fill={fill}
                          rx="3"
                          className={`sessions__viz-bar ${isNy ? 'sessions__viz-bar--ny' : ''}`}
                          style={{ '--bar-i': hr }}
                          filter={isNy ? 'url(#sessNyGlow)' : undefined}
                        />
                      );
                    })}

                    {/* NY bracket underline — marks the trading window */}
                    <g className="sessions__viz-bracket">
                      <line
                        x1={toX(9.5) + 4}
                        y1={padT + chartH + 10}
                        x2={toX(16) - 4}
                        y2={padT + chartH + 10}
                        stroke="#34d399"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        opacity="0.9"
                      />
                      <line x1={toX(9.5) + 4} y1={padT + chartH + 7} x2={toX(9.5) + 4} y2={padT + chartH + 13} stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" opacity="0.9" />
                      <line x1={toX(16) - 4} y1={padT + chartH + 7} x2={toX(16) - 4} y2={padT + chartH + 13} stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" opacity="0.9" />
                    </g>

                    {/* X-axis hour ticks */}
                    {[
                      { hr: 0, label: '12a' },
                      { hr: 6, label: '6a' },
                      { hr: 9.5, label: '9:30' },
                      { hr: 12, label: '12p' },
                      { hr: 16, label: '4p' },
                      { hr: 20, label: '8p' },
                    ].map(({ hr, label }, i) => {
                      const inNy = hr >= 9.5 && hr <= 16;
                      return (
                        <text
                          key={i}
                          x={toX(hr)}
                          y={H - 22}
                          textAnchor="middle"
                          fill={inNy ? 'rgba(52, 211, 153, 0.85)' : 'rgba(255,255,255,0.35)'}
                          fontSize="9.5"
                          fontWeight={inNy ? '700' : '500'}
                          fontFamily="Inter, system-ui, sans-serif"
                          letterSpacing="0.02em"
                        >
                          {label}
                        </text>
                      );
                    })}

                    {/* Session band labels */}
                    <g>
                      <text x={toX(1.5)} y={H - 4} textAnchor="middle" fill="rgba(255,255,255,0.28)" fontSize="8.5" fontWeight="600" letterSpacing="0.18em" fontFamily="Inter, system-ui, sans-serif">ASIAN</text>
                      <text x={toX(6.25)} y={H - 4} textAnchor="middle" fill="rgba(129, 140, 248, 0.75)" fontSize="8.5" fontWeight="600" letterSpacing="0.18em" fontFamily="Inter, system-ui, sans-serif">LONDON</text>
                      <text x={toX(12.75)} y={H - 4} textAnchor="middle" fill="#34d399" fontSize="9" fontWeight="800" letterSpacing="0.18em" fontFamily="Inter, system-ui, sans-serif">NEW YORK</text>
                      <text x={toX(20)} y={H - 4} textAnchor="middle" fill="rgba(255,255,255,0.28)" fontSize="8.5" fontWeight="600" letterSpacing="0.18em" fontFamily="Inter, system-ui, sans-serif">ASIAN</text>
                    </g>

                    {/* Live "NOW" vertical marker — updates in its own
                        component so only the tiny line/label re-renders. */}
                    <LiveNowMarker
                      padL={padL}
                      padT={padT}
                      chartH={chartH}
                      chartW={chartW}
                      hoursCount={hoursCount}
                    />
                  </svg>

                  <div className="sessions__viz-stats">
                    <div className="sessions__viz-stat">
                      <span className="sessions__viz-stat-icon sessions__viz-stat-icon--accent">
                        <Droplets size={14} strokeWidth={2.2} />
                      </span>
                      <span className="sessions__viz-stat-val sessions__viz-stat-val--accent">70%+</span>
                      <span className="sessions__viz-stat-label">of daily volume</span>
                    </div>
                    <div className="sessions__viz-stat-sep" />
                    <div className="sessions__viz-stat">
                      <span className="sessions__viz-stat-icon">
                        <Clock size={14} strokeWidth={2.2} />
                      </span>
                      <span className="sessions__viz-stat-val">6.5<span className="sessions__viz-stat-unit">hr</span></span>
                      <span className="sessions__viz-stat-label">our window</span>
                    </div>
                    <div className="sessions__viz-stat-sep" />
                    <div className="sessions__viz-stat">
                      <span className="sessions__viz-stat-icon">
                        <Bell size={14} strokeWidth={2.2} />
                      </span>
                      <span className="sessions__viz-stat-val">9:30<span className="sessions__viz-stat-unit">ET</span></span>
                      <span className="sessions__viz-stat-label">session open</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* How It Works */}
      {(() => {
        const [hiwRef, hiwInView] = useInView();
        return (
          <section className={`hiw ${hiwInView ? 'hiw--visible' : ''}`} ref={hiwRef}>
            <div className="hiw__grid-bg" />
            <div className="hiw__glow hiw__glow--1" />
            <div className="hiw__glow hiw__glow--2" />

            <div className="hiw__inner">
              <div className="hiw__badge">
                <Zap size={14} />
                Get started in minutes
              </div>
              <h2 className="hiw__heading">
                Three steps to<br />
                <span className="hiw__heading-accent">consistent profits</span>
              </h2>
              <p className="hiw__sub">No complexity. No learning curve. Just follow along.</p>

              <div className="hiw__steps">
                {/* Step 1 */}
                <div className="hiw__card hiw__card--purple" style={{'--card-i': 0}}>
                  <div className="hiw__card-glow" />
                  <div className="hiw__card-border" />
                  <span className="hiw__card-num">01</span>
                  <div className="hiw__card-content">
                    <div className="hiw__card-icon hiw__card-icon--purple">
                      <Activity size={18} />
                    </div>
                    <h3 className="hiw__card-title">The algorithm watches</h3>
                    <p className="hiw__card-desc">
                      Our ML model scans the 5-min Nasdaq chart tick-by-tick, hunting the exact setups it was trained on &mdash; bar after bar, all session.
                    </p>
                    <div className="hiw__card-visual">
                      <div className="hiw__mock-scan">
                        <div className="hiw__mock-scan-head">
                          <span className="hiw__mock-scan-pair">MNQ &middot; 5m</span>
                          <span className="hiw__mock-scan-status">
                            <span className="hiw__mock-scan-dot" />
                            SCANNING
                          </span>
                        </div>
                        <div className="hiw__mock-scan-chart">
                          <svg viewBox="0 0 220 60" preserveAspectRatio="none" className="hiw__mock-scan-svg">
                            <defs>
                              <linearGradient id="hiwScanArea" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.3" />
                                <stop offset="100%" stopColor="#a78bfa" stopOpacity="0" />
                              </linearGradient>
                            </defs>
                            <path
                              d="M0,42 L18,38 L36,40 L54,32 L72,36 L90,28 L108,30 L126,22 L144,26 L162,18 L180,22 L198,14 L220,18"
                              fill="none"
                              stroke="#a78bfa"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M0,42 L18,38 L36,40 L54,32 L72,36 L90,28 L108,30 L126,22 L144,26 L162,18 L180,22 L198,14 L220,18 L220,60 L0,60 Z"
                              fill="url(#hiwScanArea)"
                            />
                          </svg>
                          <div className="hiw__mock-scan-line" />
                        </div>
                        <div className="hiw__mock-scan-rows">
                          <div className="hiw__mock-scan-row">
                            <span className="hiw__mock-scan-label">Trend</span>
                            <span className="hiw__mock-scan-val hiw__mock-scan-val--ok">bullish</span>
                          </div>
                          <div className="hiw__mock-scan-row">
                            <span className="hiw__mock-scan-label">Momentum</span>
                            <span className="hiw__mock-scan-val hiw__mock-scan-val--ok">+0.62σ</span>
                          </div>
                          <div className="hiw__mock-scan-row">
                            <span className="hiw__mock-scan-label">Setup</span>
                            <span className="hiw__mock-scan-val hiw__mock-scan-val--wait">
                              <span className="hiw__mock-scan-pulse" />
                              watching&hellip;
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Arrow connector */}
                <div className="hiw__arrow hiw__arrow--cyan">
                  <ArrowRight size={22} strokeWidth={2.5} />
                </div>

                {/* Step 2 */}
                <div className="hiw__card hiw__card--cyan" style={{'--card-i': 1}}>
                  <div className="hiw__card-glow" />
                  <div className="hiw__card-border" />
                  <span className="hiw__card-num">02</span>
                  <div className="hiw__card-content">
                    <div className="hiw__card-icon hiw__card-icon--cyan">
                      <Target size={18} />
                    </div>
                    <h3 className="hiw__card-title">Follow the signals</h3>
                    <p className="hiw__card-desc">
                      When a signal fires, our AI mentor calls it live in chat &mdash; entry, stop loss, and take profit. Just execute.
                    </p>
                    <div className="hiw__card-visual">
                      <div className="hiw__mock-signal">
                        <div className="hiw__mock-signal-head">
                          <span className="hiw__mock-signal-type">
                            <TrendingUp size={10} />
                            BUY
                          </span>
                          <span className="hiw__mock-signal-pair">MNQ</span>
                          <span className="hiw__mock-signal-live">
                            <span className="hiw__mock-signal-live-dot" />
                            LIVE
                          </span>
                        </div>
                        <div className="hiw__mock-signal-prices">
                          <div>
                            <span className="hiw__mock-signal-label">Entry</span>
                            <span className="hiw__mock-signal-val">21,382</span>
                          </div>
                          <div>
                            <span className="hiw__mock-signal-label">SL</span>
                            <span className="hiw__mock-signal-val hiw__mock-signal-val--red">21,370</span>
                          </div>
                          <div>
                            <span className="hiw__mock-signal-label">TP</span>
                            <span className="hiw__mock-signal-val hiw__mock-signal-val--green">21,415</span>
                          </div>
                        </div>
                        <div className="hiw__mock-signal-foot">
                          <Clock size={10} />
                          <span>10:47 AM ET</span>
                          <span className="hiw__mock-signal-rr">2.75R</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Arrow connector */}
                <div className="hiw__arrow hiw__arrow--green">
                  <ArrowRight size={22} strokeWidth={2.5} />
                </div>

                {/* Step 3 */}
                <div className="hiw__card hiw__card--green" style={{'--card-i': 2}}>
                  <div className="hiw__card-glow" />
                  <div className="hiw__card-border" />
                  <span className="hiw__card-num">03</span>
                  <div className="hiw__card-content">
                    <div className="hiw__card-icon hiw__card-icon--green">
                      <TrendingUp size={18} />
                    </div>
                    <h3 className="hiw__card-title">Stack consistent profits</h3>
                    <p className="hiw__card-desc">
                      ~3 trades per day during the NY session. Follow the plan, manage your risk, grow your account.
                    </p>
                    <div className="hiw__card-visual">
                      <div className="hiw__mock-profit">
                        <div className="hiw__mock-profit-head">
                          <div>
                            <span className="hiw__mock-profit-label">This month</span>
                            <span className="hiw__mock-profit-val">+$4,650</span>
                          </div>
                          <span className="hiw__mock-profit-delta">
                            <TrendingUp size={10} />
                            +12.4%
                          </span>
                        </div>
                        <svg viewBox="0 0 200 64" className="hiw__mock-profit-curve" preserveAspectRatio="none">
                          <defs>
                            <linearGradient id="hiwCurveFill" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#34d399" stopOpacity="0.35" />
                              <stop offset="100%" stopColor="#34d399" stopOpacity="0" />
                            </linearGradient>
                            <linearGradient id="hiwCurveStroke" x1="0" y1="0" x2="1" y2="0">
                              <stop offset="0%" stopColor="#22d3ee" />
                              <stop offset="100%" stopColor="#34d399" />
                            </linearGradient>
                          </defs>
                          <path
                            d="M0 54 L22 48 L44 52 L66 42 L88 36 L110 40 L132 28 L154 22 L176 18 L200 10"
                            fill="none"
                            stroke="url(#hiwCurveStroke)"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="hiw__mock-profit-line"
                          />
                          <path
                            d="M0 54 L22 48 L44 52 L66 42 L88 36 L110 40 L132 28 L154 22 L176 18 L200 10 L200 64 L0 64 Z"
                            fill="url(#hiwCurveFill)"
                            className="hiw__mock-profit-area"
                          />
                          <circle cx="200" cy="10" r="3.5" fill="#0a1628" stroke="#34d399" strokeWidth="1.8" className="hiw__mock-profit-dot" />
                        </svg>
                        <div className="hiw__mock-profit-foot">
                          <span>4 weeks</span>
                          <span className="hiw__mock-profit-trades">64 trades &middot; 74% win</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button className="hiw__cta" onClick={handleGetStarted}>
                Join the live room &mdash; free
                <ArrowRight size={17} />
              </button>
            </div>
          </section>
        );
      })()}

      {/* Testimonials */}
      {(() => {
        const [testRef, testInView] = useInView();
        return (
          <section className={`testimonials ${testInView ? 'testimonials--visible' : ''}`} ref={testRef}>
            <div className="testimonials__glow testimonials__glow--1" />
            <div className="testimonials__inner">
              <h2 className="testimonials__heading">What traders are saying</h2>
              <p className="testimonials__sub">Real feedback from real members.</p>

              <div className="testimonials__grid">
                {testimonials.map((t, i) => (
                  <div key={i} className="testimonial" style={{'--t-i': i}}>
                    <div className="testimonial__quote-mark">&ldquo;</div>
                    <div className="testimonial__stars">
                      {[...Array(t.rating)].map((_, j) => (
                        <Star key={j} size={13} fill="#f59e0b" stroke="#f59e0b" />
                      ))}
                    </div>
                    <p className="testimonial__text">{t.text}</p>
                    <div className="testimonial__divider" />
                    <div className="testimonial__author">
                      <div className="testimonial__avatar">
                        {t.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div className="testimonial__name">{t.name}</div>
                        <div className="testimonial__role">{t.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* Guarantee */}
      <section className="guarantee">
        <div className="guarantee__inner">
          <div className="guarantee__icon"><ShieldCheck size={40} /></div>
          <h2 className="guarantee__title">Try it completely risk-free</h2>
          <p className="guarantee__desc">
            7 days free. Cancel in one click before your trial ends.
            If the signals don&rsquo;t speak for themselves in the first week, you pay nothing.
          </p>
          <button className="guarantee__cta" onClick={handleGetStarted}>
            Claim my free week
            <ArrowRight size={17} />
          </button>
        </div>
      </section>

      {/* Pricing */}
      {(() => {
        const [priceRef, priceInView] = useInView();
        const [billing, setBilling] = useState('monthly');
        const features = [
          'Live Nasdaq futures signals (NY session)',
          'AI trading mentor in live chat',
          '5 years of backtested edge',
          'Full verified trade history + CSV export',
          'Tier-based position sizing guidance',
          '7-day free trial, no card required',
          'Cancel anytime in 1 click',
        ];
        const isAnnual = billing === 'annual';
        const displayPrice = isAnnual ? 79 : 99;
        const annualTotal = 79 * 12;
        const annualSavings = 99 * 12 - annualTotal;

        return (
          <section className={`pricing ${priceInView ? 'pricing--visible' : ''}`} ref={priceRef}>
            <div className="pricing__grid-bg" />
            <div className="pricing__glow pricing__glow--1" />
            <div className="pricing__glow pricing__glow--2" />

            <div className="pricing__inner">
              <div className="pricing__badge">
                <Zap size={14} />
                No hidden fees
              </div>
              <h2 className="pricing__heading">
                One plan.<br />
                <span className="pricing__heading-accent">Full access.</span>
              </h2>
              <p className="pricing__sub">
                One MNQ point = $2. Our average winning trade covers your <strong>whole month</strong>.
              </p>

              <div className="pricing__toggle" role="tablist" aria-label="Billing frequency">
                <button
                  role="tab"
                  aria-selected={!isAnnual}
                  className={`pricing__toggle-btn ${!isAnnual ? 'pricing__toggle-btn--active' : ''}`}
                  onClick={() => setBilling('monthly')}
                >
                  Monthly
                </button>
                <button
                  role="tab"
                  aria-selected={isAnnual}
                  className={`pricing__toggle-btn ${isAnnual ? 'pricing__toggle-btn--active' : ''}`}
                  onClick={() => setBilling('annual')}
                >
                  Annual
                  <span className="pricing__toggle-save">Save ${annualSavings}</span>
                </button>
              </div>

              <div className="price-card">
                <div className="price-card__border" />
                <div className="price-card__card-glow" />
                <div className="price-card__scan" />

                <div className="price-card__top">
                  <span className="price-card__label">AlphaNQ Premium</span>
                  <div className="price-card__amount">
                    <span className="price-card__currency">$</span>
                    <span className="price-card__dollar">{displayPrice}</span>
                    <span className="price-card__period">/mo</span>
                  </div>
                  {isAnnual ? (
                    <span className="price-card__billed">
                      Billed ${annualTotal}/yr &middot; <span className="price-card__strike">$1,188</span> &middot; save ${annualSavings}
                    </span>
                  ) : (
                    <span className="price-card__billed">Billed monthly &middot; cancel anytime</span>
                  )}
                  <div className="price-card__trial-badge">
                    <Clock size={13} />
                    7 days free trial
                  </div>
                </div>

                <div className="price-card__anchor">
                  <TrendingUp size={14} />
                  <span>Avg winning trade covers a full month of access.</span>
                </div>

                <div className="price-card__divider" />

                <ul className="price-card__list">
                  {features.map((f, i) => (
                    <li key={i} style={{'--li-i': i}}>
                      <span className="price-card__check"><Check size={15} /></span>
                      {f}
                    </li>
                  ))}
                </ul>

                <div className="price-card__guarantee-box">
                  <ShieldCheck size={20} />
                  <div>
                    <span className="price-card__guarantee-title">7-day risk-free trial</span>
                    <span className="price-card__guarantee-sub">No credit card required. Cancel in 1 click.</span>
                  </div>
                </div>

                <div className="price-card__bottom">
                  <button className="price-card__cta" onClick={handleGetStarted}>
                    {isAnnual ? 'Start annual \u2014 no card today' : 'Start my trial \u2014 no card'}
                    <ArrowRight size={16} />
                  </button>
                  <span className="price-card__guarantee">
                    <Check size={13} />
                    Join 2,400+ traders in the live room
                  </span>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* CTA Banner */}
      {(() => {
        const [ctaRef, ctaInView] = useInView();
        return (
          <section className={`cta-banner ${ctaInView ? 'cta-banner--visible' : ''}`} ref={ctaRef}>
            <div className="cta-banner__bg" />
            <div className="cta-banner__orb cta-banner__orb--1" />
            <div className="cta-banner__orb cta-banner__orb--2" />
            <div className="cta-banner__orb cta-banner__orb--3" />

            <div className="cta-banner__inner">
              <div className="cta-banner__urgency">
                <span className="cta-banner__urgency-dot" />
                Markets open &middot; next signal could fire any minute
              </div>
              <h2 className="cta-banner__title">
                Every day you wait is a<br />day of <span className="cta-banner__title-accent">missed signals</span>
              </h2>
              <p className="cta-banner__desc">
                Traders who joined last month averaged <strong>+$4,650</strong>. Your free trial starts now
                &mdash; and one winning trade pays for your entire year.
              </p>

              <div className="cta-banner__stats">
                <div className="cta-banner__stat">
                  <span className="cta-banner__stat-val">73.4%</span>
                  <span className="cta-banner__stat-label">Win rate</span>
                </div>
                <div className="cta-banner__stat-sep" />
                <div className="cta-banner__stat">
                  <span className="cta-banner__stat-val">5 yrs</span>
                  <span className="cta-banner__stat-label">Backtested</span>
                </div>
                <div className="cta-banner__stat-sep" />
                <div className="cta-banner__stat">
                  <span className="cta-banner__stat-val">$0</span>
                  <span className="cta-banner__stat-label">To start</span>
                </div>
              </div>

              <button className="cta-banner__btn" onClick={handleGetStarted}>
                Get today&rsquo;s signals free
                <ArrowRight size={17} />
              </button>
              <span className="cta-banner__fine">7 days free &middot; Cancel anytime &middot; No credit card</span>
            </div>
          </section>
        );
      })()}

      {/* FAQ */}
      <section className="faq-section">
        <div className="faq-section__inner">
          <h2 className="section-title">Frequently asked questions</h2>
          <p className="section-subtitle">Everything you need to know before you subscribe</p>
          <div className="faq-list">
            {faqs.map((item, i) => (
              <FaqItem key={i} item={item} />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="lfooter">
        <div className="lfooter__top-border" />
        <div className="lfooter__inner">
          <div className="lfooter__main">
            <div className="lfooter__brand">
              <img src="/logo.png" alt="AlphaNQ" className="lfooter__brand-img" />
            </div>

            <nav className="lfooter__nav" aria-label="Footer">
              <span className="lfooter__link" onClick={() => {
                const el = document.querySelector('.pricing');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}>Pricing</span>
              <span className="lfooter__link" onClick={() => {
                const el = document.querySelector('.performance');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}>Performance</span>
              <span className="lfooter__link" onClick={() => {
                const el = document.querySelector('.faq-section');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}>FAQ</span>
              <span className="lfooter__link" onClick={() => navigate('/login')}>Sign in</span>
            </nav>

            <div className="lfooter__socials" aria-label="Social links">
              <a href="#" className="lfooter__social" aria-label="X (Twitter)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a href="#" className="lfooter__social" aria-label="Discord">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z" />
                </svg>
              </a>
              <a href="mailto:hello@alphanq.com" className="lfooter__social" aria-label="Email">
                <Mail size={14} />
              </a>
            </div>
          </div>

          <div className="lfooter__bottom">
            <span className="lfooter__copy">
              &copy; {new Date().getFullYear()} AlphaNQ, Inc.
            </span>
            <div className="lfooter__legal">
              <span className="lfooter__link-sm">Terms</span>
              <span className="lfooter__link-sm">Privacy</span>
              <span className="lfooter__link-sm">Risk disclosure</span>
            </div>
          </div>

          <p className="lfooter__disclaimer">
            Trading futures involves substantial risk and is not suitable for all investors. Past performance is not indicative of future results. Educational use only &mdash; not financial advice.
          </p>
        </div>
      </footer>

      {/* Floating elements */}

      <ModalManager />
      <ToastContainer />
    </div>
  );
};

export default LandingPage;
